import React, { useEffect, useState, useCallback } from "react";
import { FaFileInvoiceDollar, FaPaperPlane, FaSearch, FaFilter, FaEye, FaEnvelope } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "universal-cookie";
import moment from "moment";
import "../../css/TelefonosNotificacion.css";
import "../../css/Usuarios.css";
import "../../css/Facturacion.css";



const Facturacion = () => {
  const cookies = new Cookies();
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // ── Filtros ──────────────────────────────────────────────
  const [busqueda,     setBusqueda]     = useState("");
  const [fechaInicio,  setFechaInicio]  = useState("");
  const [fechaFin,     setFechaFin]     = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [estadoCorreoFiltro, setEstadoCorreoFiltro] = useState("");

  // ── Datos ─────────────────────────────────────────────────
  const [datos,          setDatos]          = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [paginaActual,   setPaginaActual]   = useState(1);
  const elementosPorPagina = 10;

  // ── Loading por fila ──────────────────────────────────────
  const [loadingRows, setLoadingRows] = useState({});

  // ── Fila activa (resaltado visual mientras se procesa) ────
  const [filaActiva, setFilaActiva] = useState(null);

  // ── Modal detalle ─────────────────────────────────────────
  const [modalVisible,   setModalVisible]   = useState(false);
  const [facturaDetalle, setFacturaDetalle] = useState(null);

  // ─────────────────────────────────────────────────────────
  // Cargar datos
  // ─────────────────────────────────────────────────────────
  const fetchFacturas = useCallback(async (mantenerPagina = false) => {
    try {
      const response = await axios.get(`${baseURL}/api/Facturacion/ListadoLiquidaciones`);
      setDatos(response.data || []);
      // Si no queremos mantener la página, reiniciar a 1
      if (!mantenerPagina) {
        setPaginaActual(1);
      }
    } catch (error) {
      console.error("Error al obtener liquidaciones:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Error al cargar las liquidaciones",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  }, [baseURL]);

  useEffect(() => { fetchFacturas(); }, [fetchFacturas]);

  // ─────────────────────────────────────────────────────────
  // Filtrado reactivo
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    const filtrados = datos.filter((item) => {
      const txt = busqueda.toLowerCase();
      const coincideTexto =
        !busqueda ||
        item.cliente?.toLowerCase().includes(txt) ||
        item.correo?.toLowerCase().includes(txt) ||
        item.servicio?.toLowerCase().includes(txt) ||
        (item.nroFactura || "").toLowerCase().includes(txt) ||
        String(item.idOrden).includes(txt);

      const coincideEstado = !estadoFiltro || item.estadoFactura === estadoFiltro;
      const coincideEstadoCorreo = !estadoCorreoFiltro || item.estadoCorreo === estadoCorreoFiltro;
      const fecha = moment(item.fecha);
      const coincideInicio = !fechaInicio || fecha.isSameOrAfter(moment(fechaInicio));
      const coincideFin    = !fechaFin    || fecha.isSameOrBefore(moment(fechaFin));

      return coincideTexto && coincideEstado && coincideEstadoCorreo && coincideInicio && coincideFin;
    });
    setDatosFiltrados(filtrados);
    // Solo resetear a página 1 si cambiaron los filtros (no cuando solo se recargan los datos)
  }, [datos, busqueda, estadoFiltro, estadoCorreoFiltro, fechaInicio, fechaFin]);

  // Efecto separado para resetear paginación solo cuando cambian los filtros
  useEffect(() => {
    setPaginaActual(1);
  }, [busqueda, estadoFiltro, estadoCorreoFiltro, fechaInicio, fechaFin]);

  // ─────────────────────────────────────────────────────────
  // Helpers loading
  // ─────────────────────────────────────────────────────────
  const setRowLoading = (id, key, value) =>
    setLoadingRows((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const isGenerando = (id) => loadingRows[id]?.generando || false;
  const isEnviando  = (id) => loadingRows[id]?.enviando  || false;
  const isEnviandoCorreo = (id) => loadingRows[id]?.enviandoCorreo || false;

  // ─────────────────────────────────────────────────────────
  // Descargar Factura PDF
  // GET /api/Facturacion/DescargarFactura?nroFactura=X
  // ─────────────────────────────────────────────────────────
  const descargarFactura = async (nroFactura) => {
    if (!nroFactura) {
      Swal.fire({
        icon: "warning",
        title: "Factura no disponible",
        text: "Esta orden no tiene un número de factura generado.",
        confirmButtonColor: "#1a56db",
      });
      return;
    }

    try {
      // Llamar al endpoint de descarga
      const response = await axios.get(
        `${baseURL}/api/Facturacion/DescargarFactura?nroFactura=${nroFactura}`,
        {
          responseType: 'blob', // Importante para archivos binarios (PDF)
        }
      );

      // Crear un blob del PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Crear URL temporal del blob
      const url = window.URL.createObjectURL(blob);
      
      // Crear link temporal para descarga
      const link = document.createElement('a');
      link.href = url;
      link.download = `Factura_${nroFactura}.pdf`;
      
      // Simular click para descargar
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Factura descargada correctamente",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

    } catch (error) {
      console.error("Error al descargar factura:", error);
      
      const mensajeError = 
        error.response?.data?.mensaje || 
        error.response?.status === 404 
          ? "Factura no encontrada en el servidor" 
          : "No se pudo descargar la factura. Por favor, intente nuevamente.";
      
      Swal.fire({
        icon: "error",
        title: "Error al descargar",
        text: mensajeError,
        confirmButtonColor: "#1a56db",
      });
    }
  };

  // ─────────────────────────────────────────────────────────
  // Generar Factura
  // POST /api/Facturacion/GenerarFactura
  // Payload: { suscripcionId, idIntentoPago, usuarioCrea }
  // Luego recarga la grilla para obtener el idLiquidacion generado
  // ─────────────────────────────────────────────────────────
  const generarFactura = async (orden) => {
    setFilaActiva(orden.idOrden);
    setRowLoading(orden.idOrden, "generando", true);
    try {
      await axios.post(`${baseURL}/api/Facturacion/GenerarFactura`, {
        suscripcionId: Number(orden.suscripcionId),
        idIntentoPago: Number(orden.intentoPagoId),
        usuarioCrea:   cookies.get("nombreUsuario") || "ADMIN",
      });

      // Recargar grilla manteniendo la página actual
      await fetchFacturas(true);

      Swal.fire({
        toast: true, position: "top-end", icon: "success",
        title: "Factura generada correctamente",
        showConfirmButton: false, timer: 3000, timerProgressBar: true,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error", title: "Error al generar",
        text: error.response?.data?.message || "Ocurrió un error al generar la factura.",
      });
    } finally {
      setRowLoading(orden.idOrden, "generando", false);
      // Mantener resaltado 3s para que el usuario vea el registro actualizado
      setTimeout(() => setFilaActiva(null), 3000);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Enviar Factura
  // Paso 1 → POST /api/Facturacion/GenerarCufeQr?idLiquidacion=X
  // Paso 2 → POST /api/Facturacion/EnviarFacturaDian?idLiquidacion=X?usuEnvia=admin (CON VALIDACIÓN)
  // Paso 3 → POST /api/Facturacion/GenerarPdfFactura?idLiquidacion=X
  // ─────────────────────────────────────────────────────────
  const enviarFactura = async (orden) => {
    const confirm = await Swal.fire({
      title: "Confirmar envío a la DIAN",
        html: `¿Confirma que desea enviar la factura <b>${orden.nroFactura}</b> a la DIAN?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1a56db",
    });
    if (!confirm.isConfirmed) return;

    const idLiquidacion = orden.idLiquidacion;

    if (!idLiquidacion) {
      Swal.fire({ 
        icon: "warning", 
        title: "Sin liquidación", 
        text: "Esta orden no tiene un ID de liquidación. Genera la factura primero." 
      });
      return;
    }

    setFilaActiva(orden.idOrden);
    setRowLoading(orden.idOrden, "enviando", true);
    
    try {
      // ═══════════════════════════════════════════════════════
      // PASO 1: Generar CUFE y QR
      // ═══════════════════════════════════════════════════════
      await axios.post(`${baseURL}/api/Facturacion/GenerarCufeQr?idLiquidacion=${idLiquidacion}`);

      // ═══════════════════════════════════════════════════════
      // PASO 2: Enviar a la DIAN (CON VALIDACIÓN DE RESPUESTA)
      // ═══════════════════════════════════════════════════════
      let envioExitoso = false;
      try {
        const responseDian = await axios.post(
          `${baseURL}/api/Facturacion/EnviarFacturaDian?idLiquidacion=${idLiquidacion}&usuEnvia=${cookies.get("nombreUsuario")}`
        );
        
        // Verificar si la respuesta fue exitosa
        if (responseDian.data?.status === true) {
          envioExitoso = true;
        } else {
          // Si status es false, mostrar el mensaje de error
          throw new Error(responseDian.data?.mensaje || "Error desconocido al enviar a DIAN");
        }
      } catch (errorDian) {
        // Capturar errores de la petición o del backend
        const mensajeError = 
          errorDian.response?.data?.mensaje || 
          errorDian.message || 
          "No fue posible enviar la factura a la DIAN";
        
        Swal.fire({
          icon: "error",
          title: "Error al enviar a la DIAN",
          text: mensajeError,
          confirmButtonColor: "#1a56db",
        });
        
        // Detener el proceso aquí, NO continuar al Paso 3
        return;
      }

      // ═══════════════════════════════════════════════════════
      // PASO 3: Generar PDF (solo si el Paso 2 fue exitoso)
      // ═══════════════════════════════════════════════════════
      if (envioExitoso) {
        await axios.post(`${baseURL}/api/Facturacion/GenerarPdfFactura?idLiquidacion=${idLiquidacion}`);

        // Recargar grilla manteniendo la página actual
        await fetchFacturas(true);

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Factura enviada a la DIAN y PDF generado correctamente",
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
        });
      }

    } catch (error) {
      // Errores generales (Paso 1 o Paso 3)
      console.error("Error en el proceso de envío:", error);
      const mensajeError = 
        error.response?.data?.mensaje || 
        error.response?.data?.message || 
        error.message || 
        "Ocurrió un error durante el proceso de envío";
      
      Swal.fire({
        icon: "error",
        title: "Error en el proceso",
        text: mensajeError,
        confirmButtonColor: "#1a56db",
      });
    } finally {
      setRowLoading(orden.idOrden, "enviando", false);
      setTimeout(() => setFilaActiva(null), 3000);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Enviar Factura por Correo
  // POST /api/Facturacion/EnviarFacturaCorreo?factura=X&usuario=Y
  // ─────────────────────────────────────────────────────────
  const enviarFacturaCorreo = async (orden) => {
    const confirm = await Swal.fire({
      title: "Confirmar envío por correo",
      html: `¿Confirma que desea enviar la factura <b>${orden.nroFactura}</b> por correo electrónico?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1a56db",
    });
    if (!confirm.isConfirmed) return;

    setFilaActiva(orden.idOrden);
    setRowLoading(orden.idOrden, "enviandoCorreo", true);
    try {
      const nroFacturaSinGuion = orden.nroFactura.replace(/-/g, "");
      await axios.post(
        `${baseURL}/api/Facturacion/EnviarFacturaCorreo?factura=${nroFacturaSinGuion}&usuario=${cookies.get("nombreUsuario")}`
      );

      await fetchFacturas(true);

      Swal.fire({
        toast: true, position: "top-end", icon: "success",
        title: "Factura enviada por correo correctamente",
        showConfirmButton: false, timer: 3000, timerProgressBar: true,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error", title: "Error al enviar correo",
        text: error.response?.data?.message || error.response?.data?.mensaje || "Ocurrió un error al enviar la factura por correo.",
      });
    } finally {
      setRowLoading(orden.idOrden, "enviandoCorreo", false);
      setTimeout(() => setFilaActiva(null), 3000);
    }
  };

  // ── Modal ─────────────────────────────────────────────────
  const verDetalle = (orden) => { setFacturaDetalle(orden); setModalVisible(true); };

  // ─────────────────────────────────────────────────────────
  // Paginación
  // ─────────────────────────────────────────────────────────
  const totalPaginas   = Math.ceil(datosFiltrados.length / elementosPorPagina);
  const indexInicio    = (paginaActual - 1) * elementosPorPagina;
  const datosPaginados = datosFiltrados.slice(indexInicio, indexInicio + elementosPorPagina);

  // Rango de páginas visibles (máx 5)
  const rangoInicio     = Math.max(1, Math.min(paginaActual - 2, totalPaginas - 4));
  const rangoFin        = Math.min(totalPaginas, rangoInicio + 4);
  const paginasVisibles = Array.from({ length: rangoFin - rangoInicio + 1 }, (_, i) => rangoInicio + i);

  // ── Helpers UI ────────────────────────────────────────────
  const formatCurrency = (v) =>
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(v);

  const badgeEstado = (e) =>
    ({ Pendiente: "badge-pendiente", Generada: "badge-generada", Enviada: "badge-enviada" }[e] || "badge-pendiente");

  const badgeEstadoCorreo = (e) =>
    ({ Pendiente: "badge-correo-pendiente", Enviado: "badge-correo-enviado" }[e] || "badge-correo-pendiente");

  const limpiarFiltros = () => { setBusqueda(""); setFechaInicio(""); setFechaFin(""); setEstadoFiltro(""); setEstadoCorreoFiltro(""); };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────
  return (
    <div className="telefonos-container">
      <div className="telefonos-card">

        {/* ── Header ── */}
        <div className="card-header-custom d-flex align-items-center justify-content-between">
          <h3 className="card-title-custom">
            <i className="fas fa-file-invoice-dollar mr-2"></i>
            Facturación
          </h3>
          <span className="fact-badge-count">
            {datosFiltrados.length} registro{datosFiltrados.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="form-section">

          {/* ── Filtros ── */}
          <div className="fact-filtros-wrap">
            {/* Buscador */}
            <div className="usuarios-search-wrap fact-search">
              <FaSearch className="usuarios-search-icon" />
              <input
                type="text"
                className="custom-input usuarios-search-input"
                placeholder="Buscar por cliente, correo, Nro. Factura..."
                autoComplete="off"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>

            {/* Desde */}
            <div className="fact-filter-item fact-filter-date">
              <label className="fact-filter-label">Desde</label>
              <input
                type="date"
                className="custom-input fact-date-input"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
              />
            </div>

            {/* Hasta */}
            <div className="fact-filter-item fact-filter-date">
              <label className="fact-filter-label">Hasta</label>
              <input
                type="date"
                className="custom-input fact-date-input"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
              />
            </div>

            {/* Estado */}
            <div className="fact-filter-item fact-filter-estado">
              <label className="fact-filter-label">Estado</label>
              <select
                className="custom-input fact-date-input"
                value={estadoFiltro}
                onChange={(e) => setEstadoFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Generada">Generada</option>
                <option value="Enviada">Enviada</option>
              </select>
            </div>

            {/* Estado Correo */}
            <div className="fact-filter-item fact-filter-estado">
              <label className="fact-filter-label">Estado Correo</label>
              <select
                className="custom-input fact-date-input"
                value={estadoCorreoFiltro}
                onChange={(e) => setEstadoCorreoFiltro(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Enviado">Enviado</option>
              </select>
            </div>

            {/* Limpiar */}
            <button className="btn-limpiar-filtros" onClick={limpiarFiltros}>
              <FaFilter style={{ marginRight: 5 }} /> Limpiar
            </button>
          </div>

          {/* ── Tabla ── */}
          <div className="table-responsive usuarios-table-wrap">
            <table className="usuarios-table fact-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nro. Factura</th>
                  <th>Cliente</th>
                  <th>Servicio</th>
                  <th>Fecha</th>
                  <th>Total</th>
                  <th>Estado</th>
                  <th>Estado Correo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {datosPaginados.length > 0 ? (
                  datosPaginados.map((item) => (
                    <tr
                      key={item.idOrden}
                      className={filaActiva === item.idOrden ? "row-procesando" : ""}
                    >

                      <td className="fact-id">#{item.idOrden}</td>

                      {/* Nro Factura */}
                      <td>
                        {item.nroFactura
                          ? <span className="fact-nro-badge">{item.nroFactura}</span>
                          : <span className="fact-nro-empty">—</span>}
                      </td>

                      <td>
                        <div className="fact-cliente-name">{item.cliente}</div>
                        <div className="fact-cliente-tel">{item.telefono}</div>
                      </td>
                      <td>{item.servicio}</td>
                      <td>{moment(item.fecha).format("DD/MM/YYYY")}</td>
                      <td><span className="fact-total">{formatCurrency(item.total)}</span></td>

                      <td>
                        <span className={`badge-estado ${badgeEstado(item.estadoFactura)}`}>
                          <span className="badge-dot"></span>
                          {item.estadoFactura}
                        </span>
                      </td>

                      <td>
                        <span className={`badge-estado ${badgeEstadoCorreo(item.estadoCorreo)}`}>
                          <span className="badge-dot"></span>
                          {item.estadoCorreo || "Pendiente"}
                        </span>
                      </td>

                      <td>
                        <div className="acciones-wrap">
                          <button className="btn-accion-tabla btn-ver" title="Ver detalle" onClick={() => verDetalle(item)}>
                            <FaEye /> Ver
                          </button>
                          <button
                            className="btn-accion-tabla btn-generar"
                            disabled={item.estadoFactura !== "Pendiente" || isGenerando(item.idOrden)}
                            onClick={() => generarFactura(item)}
                          >
                            {isGenerando(item.idOrden) ? <span className="fact-spinner" /> : <FaFileInvoiceDollar />}
                            {isGenerando(item.idOrden) ? "Generando..." : "Generar"}
                          </button>
                          <button
                            className="btn-accion-tabla btn-enviar"
                            disabled={item.estadoFactura === "Pendiente" || item.facturaEnviada || isEnviando(item.idOrden)}
                            onClick={() => enviarFactura(item)}
                          >
                            {isEnviando(item.idOrden) ? <span className="fact-spinner" /> : <FaPaperPlane />}
                            {isEnviando(item.idOrden) ? "Enviando..." : item.facturaEnviada ? "Enviar" : "Enviar"}
                          </button>
                          {item.estadoFactura === "Enviada" && (
                            <button
                              className="btn-accion-tabla btn-enviar-correo"
                              disabled={isEnviandoCorreo(item.idOrden)}
                              onClick={() => enviarFacturaCorreo(item)}
                            >
                              {isEnviandoCorreo(item.idOrden) ? <span className="fact-spinner" /> : <FaEnvelope />}
                              {isEnviandoCorreo(item.idOrden) ? "Enviando..." : "Enviar Correo"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="sin-resultados">
                      <i className="fas fa-search mr-2"></i>No se encontraron registros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Paginador ── */}
          {totalPaginas > 1 && (
            <div className="fact-paginacion">
              <span className="fact-pag-info">
                Mostrando {indexInicio + 1}–{Math.min(indexInicio + elementosPorPagina, datosFiltrados.length)} de {datosFiltrados.length} registros
              </span>
              <div className="paginacion-wrap">
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPaginaActual(1)} title="Primera">«</button>
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPaginaActual((p) => p - 1)} title="Anterior">‹</button>

                {rangoInicio > 1 && <span className="btn-pagina btn-pag-dots">…</span>}

                {paginasVisibles.map((n) => (
                  <button key={n} className={`btn-pagina ${paginaActual === n ? "activa" : ""}`} onClick={() => setPaginaActual(n)}>
                    {n}
                  </button>
                ))}

                {rangoFin < totalPaginas && <span className="btn-pagina btn-pag-dots">…</span>}

                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual((p) => p + 1)} title="Siguiente">›</button>
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual(totalPaginas)} title="Última">»</button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Modal Detalle ── */}
      {modalVisible && facturaDetalle && (
        <div className="modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="modal-usuarios fact-modal-detalle" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h5 className="modal-title-custom">🧾 Detalle de Orden #{facturaDetalle.idOrden}</h5>
              <button className="modal-close" onClick={() => setModalVisible(false)}>×</button>
            </div>
            <div className="modal-body-custom">
              <div className="fact-detalle-grid">
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Nro. Factura</span>
                  <span className="fact-detalle-value">
                    {facturaDetalle.nroFactura
                      ? <span className="fact-nro-badge">{facturaDetalle.nroFactura}</span>
                      : <span className="fact-nro-empty">Sin generar</span>}
                  </span>
                </div>
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Estado</span>
                  <span className={`badge-estado ${badgeEstado(facturaDetalle.estadoFactura)}`}>
                    <span className="badge-dot"></span>{facturaDetalle.estadoFactura}
                  </span>
                </div>
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Estado Correo</span>
                  <span className={`badge-estado ${badgeEstadoCorreo(facturaDetalle.estadoCorreo)}`}>
                    <span className="badge-dot"></span>{facturaDetalle.estadoCorreo || "Pendiente"}
                  </span>
                </div>
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Cliente</span>
                  <span className="fact-detalle-value">{facturaDetalle.cliente}</span>
                </div>
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Teléfono</span>
                  <span className="fact-detalle-value">{facturaDetalle.telefono}</span>
                </div>
                <div className="fact-detalle-item fact-detalle-full">
                  <span className="fact-detalle-label">Correo</span>
                  <span className="fact-detalle-value">{facturaDetalle.correo}</span>
                </div>
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Servicio</span>
                  <span className="fact-detalle-value">{facturaDetalle.servicio}</span>
                </div>
                <div className="fact-detalle-item">
                  <span className="fact-detalle-label">Fecha</span>
                  <span className="fact-detalle-value">{moment(facturaDetalle.fecha).format("DD/MM/YYYY")}</span>
                </div>
                <div className="fact-detalle-item fact-detalle-full">
                  <span className="fact-detalle-label">Total</span>
                  <span className="fact-detalle-value fact-total" style={{ fontSize: "1.1rem" }}>{formatCurrency(facturaDetalle.total)}</span>
                </div>
                
                {/* ══════════════════════════════════════════════════════ */}
                {/* Botón de descarga PDF - Solo si tiene factura enviada */}
                {/* ══════════════════════════════════════════════════════ */}
                {facturaDetalle.nroFactura && facturaDetalle.estadoFactura === "Enviada" && (
                  <div className="fact-detalle-item fact-detalle-full">
                    <span className="fact-detalle-label">Archivo PDF</span>
                    <button 
                      className="fact-link-descarga"
                      onClick={() => descargarFactura(facturaDetalle.nroFactura)}                      
                    >
                      📄 Descargar factura PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={() => setModalVisible(false)}>Cerrar</button>
              {facturaDetalle.estadoFactura === "Pendiente" && (
                <button className="btn-save-custom text-white fact-btn-modal-generar" disabled={isGenerando(facturaDetalle.idOrden)}
                  onClick={() => { generarFactura(facturaDetalle); setModalVisible(false); }}>
                  <FaFileInvoiceDollar style={{ marginRight: 6 }} /> Generar Factura
                </button>
              )}
              {facturaDetalle.estadoFactura === "Generada" && !facturaDetalle.facturaEnviada && (
                <button className="btn-save-custom text-white fact-btn-modal-enviar" disabled={isEnviando(facturaDetalle.idOrden)}
                  onClick={() => { enviarFactura(facturaDetalle); setModalVisible(false); }}>
                  <FaPaperPlane style={{ marginRight: 6 }} /> Enviar Factura
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Facturacion;
